"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useLocale } from "next-intl";
import { FilterValue, Region } from "@/shared/types/type/search";
import { NoDataTemplates } from "@/shared/ui/templates";
import { Filter } from "@/widgets/search/ui/molecules";
import { useRankingHospitals } from "@/widgets/search-ranking/model/hooks/useRankingHospitals";
import { useSearchResultAnchorScrollRestore } from "@/features/search-result/lib/useSearchResultAnchorScrollRestore";
import { RankingHeader, RankingMetaBar, RankingTitle, RankingUnsupportedNotice } from "@/widgets/search-ranking/ui/molecules";
import { RankingHospitalList } from "@/widgets/search-ranking/ui/organisms";
import { DEFAULT_RANKING_REGION_ID, DEFAULT_RANKING_TREATMENT_ID } from "@/widgets/search-ranking/lib/rankingQuery";
import { buildHospitalShortPath } from "@/entities/hospital/lib/detail-route";
import type { RankingHospitalItem } from "@/widgets/search-ranking/model/types";
interface SearchRankingTemplateProps {
  regionList: Region[];
  treatmentList: FilterValue[];
}
const SearchRankingTemplate = t0 => {
  const $ = _c(53);
  const {
    regionList,
    treatmentList
  } = t0;
  const locale = useLocale();
  const params = useSearchParams();
  let t1;
  if ($[0] !== params) {
    t1 = params?.get("region") ?? "";
    $[0] = params;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const regionQuery = t1;
  let t2;
  if ($[2] !== params) {
    t2 = params?.get("treatment") ?? "";
    $[2] = params;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const treatmentQuery = t2;
  const querySignature = `ranking|r=${regionQuery}|t=${treatmentQuery}`;
  let t3;
  if ($[4] !== regionList) {
    t3 = {
      regionList
    };
    $[4] = regionList;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const {
    items,
    totalReviewCount,
    isLoading,
    isQueryEnabled,
    isUnsupportedSelection,
    isUnsupportedRegion,
    isUnsupportedTreatment,
    supportedRegionIds,
    supportedTreatmentIds,
    selectedRegionCategoryId,
    selectedTreatmentCategoryId
  } = useRankingHospitals(t3);
  const router = useRouter();
  let t4;
  if ($[6] !== params || $[7] !== router) {
    t4 = (regionCategoryId, treatmentCategoryId) => {
      const next = new URLSearchParams(params?.toString() ?? "");
      next.set("region", String(regionCategoryId));
      next.set("treatment", String(treatmentCategoryId));
      router.replace(`?${next.toString()}`, {
        scroll: false
      });
    };
    $[6] = params;
    $[7] = router;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const handleSelectSupported = t4;
  const [renderedCount, setRenderedCount] = useState(20);
  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false);
  const hasNextPage = renderedCount < items.length;
  let t5;
  if ($[9] !== items.length) {
    t5 = () => {
      setIsFetchingNextPage(true);
      setRenderedCount(prev => Math.min(items.length, prev + 20));
    };
    $[9] = items.length;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const fetchNextPage = t5;
  let t6;
  let t7;
  if ($[11] !== isFetchingNextPage) {
    t6 = () => {
      if (!isFetchingNextPage) {
        return;
      }
      requestAnimationFrame(() => setIsFetchingNextPage(false));
    };
    t7 = [isFetchingNextPage];
    $[11] = isFetchingNextPage;
    $[12] = t6;
    $[13] = t7;
  } else {
    t6 = $[12];
    t7 = $[13];
  }
  useEffect(t6, t7);
  let t8;
  if ($[14] !== fetchNextPage || $[15] !== hasNextPage || $[16] !== isFetchingNextPage || $[17] !== querySignature) {
    t8 = {
      signature: querySignature,
      hasNextPage,
      isFetchingNextPage,
      fetchNextPage
    };
    $[14] = fetchNextPage;
    $[15] = hasNextPage;
    $[16] = isFetchingNextPage;
    $[17] = querySignature;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const {
    saveAnchor
  } = useSearchResultAnchorScrollRestore(t8);
  let t9;
  if ($[19] !== items || $[20] !== renderedCount) {
    t9 = items.slice(0, renderedCount);
    $[19] = items;
    $[20] = renderedCount;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const renderedItems = t9;
  let t10;
  if ($[22] !== locale) {
    t10 = item => buildHospitalShortPath(locale, item.hospitalOriginId);
    $[22] = locale;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const resolveHospitalHref = t10;
  let t11;
  let t12;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <RankingHeader />;
    t12 = <RankingTitle />;
    $[24] = t11;
    $[25] = t12;
  } else {
    t11 = $[24];
    t12 = $[25];
  }
  let t13;
  if ($[26] !== regionList || $[27] !== treatmentList) {
    t13 = <Filter regionList={regionList} treatmentList={treatmentList} hideTreatmentAllOption={true} defaultRegionId={DEFAULT_RANKING_REGION_ID} defaultTreatmentId={DEFAULT_RANKING_TREATMENT_ID} />;
    $[26] = regionList;
    $[27] = treatmentList;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== fetchNextPage || $[30] !== handleSelectSupported || $[31] !== hasNextPage || $[32] !== isFetchingNextPage || $[33] !== isLoading || $[34] !== isQueryEnabled || $[35] !== isUnsupportedRegion || $[36] !== isUnsupportedSelection || $[37] !== isUnsupportedTreatment || $[38] !== items.length || $[39] !== regionList || $[40] !== renderedItems || $[41] !== resolveHospitalHref || $[42] !== saveAnchor || $[43] !== selectedRegionCategoryId || $[44] !== selectedTreatmentCategoryId || $[45] !== supportedRegionIds || $[46] !== supportedTreatmentIds || $[47] !== totalReviewCount || $[48] !== treatmentList) {
    t14 = <div className="flex flex-1 flex-col gap-2.5 bg-gray-100 px-4 pb-4 pt-3.5">{isUnsupportedSelection ? <RankingUnsupportedNotice regionList={regionList} treatmentList={treatmentList} supportedRegionIds={supportedRegionIds} supportedTreatmentIds={supportedTreatmentIds} selectedRegionCategoryId={selectedRegionCategoryId} selectedTreatmentCategoryId={selectedTreatmentCategoryId} isUnsupportedRegion={isUnsupportedRegion} isUnsupportedTreatment={isUnsupportedTreatment} onSelect={handleSelectSupported} /> : !isQueryEnabled || !isLoading && items.length === 0 ? <NoDataTemplates pageName="search" /> : <><RankingMetaBar totalReviewCount={totalReviewCount} /><RankingHospitalList items={renderedItems} onAnchorCapture={saveAnchor} resolveHref={resolveHospitalHref} fetchNextPage={fetchNextPage} hasNextPage={hasNextPage ?? false} isFetchingNextPage={isFetchingNextPage} isLoading={isLoading} /></>}</div>;
    $[29] = fetchNextPage;
    $[30] = handleSelectSupported;
    $[31] = hasNextPage;
    $[32] = isFetchingNextPage;
    $[33] = isLoading;
    $[34] = isQueryEnabled;
    $[35] = isUnsupportedRegion;
    $[36] = isUnsupportedSelection;
    $[37] = isUnsupportedTreatment;
    $[38] = items.length;
    $[39] = regionList;
    $[40] = renderedItems;
    $[41] = resolveHospitalHref;
    $[42] = saveAnchor;
    $[43] = selectedRegionCategoryId;
    $[44] = selectedTreatmentCategoryId;
    $[45] = supportedRegionIds;
    $[46] = supportedTreatmentIds;
    $[47] = totalReviewCount;
    $[48] = treatmentList;
    $[49] = t14;
  } else {
    t14 = $[49];
  }
  let t15;
  if ($[50] !== t13 || $[51] !== t14) {
    t15 = <section className="flex min-h-screen flex-1 flex-col bg-white">{t11}{t12}{t13}{t14}</section>;
    $[50] = t13;
    $[51] = t14;
    $[52] = t15;
  } else {
    t15 = $[52];
  }
  return t15;
};
export default SearchRankingTemplate;